import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem 2rem 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 3px solid var(--primary-color);
`;

const SitemapSection = styled.div`
  margin-bottom: 1.75rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  color: #444;
  margin-bottom: 0.75rem;
  font-weight: 600;
`;

const LinkList = styled.ul`
  padding-left: 1rem;
  
  li {
    margin-bottom: 0.5rem;
    
    a {
      color: var(--primary-color);
      text-decoration: none;
      font-size: 1.1rem;
      transition: color 0.3s ease;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    p {
      margin-top: 0.25rem;
      color: #666;
      font-size: 0.9rem;
    }
  }
`;

const LastUpdated = styled.p`
  font-style: italic;
  color: #777;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const SitemapPage: React.FC = () => {
  return (
    <PageContainer className="font--jakarta">
      <SEO 
        title="Site Map"
        description="Navigate UPTREND Systems' website with our comprehensive sitemap. Find links to all pages, including our software development services, company information, and resources."
        keywords="sitemap, website navigation, UPTREND Systems sitemap, website structure, software development pages, site index"
        canonicalUrl="/sitemap"
        pageType="sitemap"
      />
      <PageTitle>Sitemap</PageTitle>
      
      <SitemapSection>
        <SectionTitle>Main Pages</SectionTitle>
        <LinkList>
          <li>
            <Link to="/">Home</Link>
            <p>Explore our services, features, and solutions for your business needs.</p>
          </li>
          <li>
            <Link to="/about">About Us</Link>
            <p>Learn about our company, mission, and expertise in custom software development.</p>
          </li>
        </LinkList>
      </SitemapSection>
      
      <SitemapSection>
        <SectionTitle>Services & Products</SectionTitle>
        <LinkList>
          <li>
            <Link to="/#features">Software Solutions</Link>
            <p>Discover our custom software solutions designed to drive business growth.</p>
          </li>
          <li>
            <Link to="/solutions/business-portals">Business Portals</Link>
            <p>Custom client and internal portals that centralize orders, documents, and reporting.</p>
          </li>
          <li>
            <Link to="/solutions/system-integrations">System Integrations</Link>
            <p>Connect your POS, CRM, accounting, and inventory into one monitored flow of data.</p>
          </li>
          <li>
            <Link to="/solutions/workflow-automation">Workflow Automation</Link>
            <p>Automate repetitive tasks, approvals, and reporting so your team focuses on growth.</p>
          </li>
          <li>
            <Link to="/documentation/kds">KDS Documentation</Link>
            <p>Complete user guide for the OPAL Kitchen Display System, including setup, workflows, and troubleshooting.</p>
          </li>
          <li>
            <Link to="/join-team">Join Our Team</Link>
            <p>Career opportunities and information about working at Uptrend Systems.</p>
          </li>
        </LinkList>
      </SitemapSection>
      
      <SitemapSection>
        <SectionTitle>Legal Information</SectionTitle>
        <LinkList>
          <li>
            <Link to="/terms">Terms of Service</Link>
            <p>Read our terms and conditions for using our website and services.</p>
          </li>
          <li>
            <Link to="/privacy">Privacy Policy</Link>
            <p>Learn how we collect, use, and protect your personal information.</p>
          </li>
          <li>
            <Link to="/cookies">Cookie Policy</Link>
            <p>Information about how we use cookies and similar technologies on our website.</p>
          </li>
        </LinkList>
      </SitemapSection>
      
      <SitemapSection>
        <SectionTitle>Contact & Support</SectionTitle>
        <LinkList>
          <li>
            <Link to="/contact">Contact Us</Link>
            <p>Get in touch with our team about your project or questions.</p>
          </li>
          <li>
            <Link to="/#newsletter">Newsletter</Link>
            <p>Subscribe to receive updates and insights from Uptrend Systems.</p>
          </li>
          <li>
            <a href="mailto:info@uptrendsystems.com">Email Us</a>
            <p>Contact our team directly with questions or inquiries.</p>
          </li>
        </LinkList>
      </SitemapSection>
      
      <LastUpdated>Last Updated: August 5, 2026</LastUpdated>
    </PageContainer>
  );
};

export default SitemapPage; 